import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { PointerEditor } from '../editor/pointer-editor.ts';
import { stallHillCoasterFixture, systemsCircuitCoasterFixture } from '../fixtures/index.ts';
import {
  FIXED_PHYSICS_STEP,
  TrackBoundDynamics,
  createTrackBoundState,
  type SimulationFrame,
  type TrackBoundState,
} from '../physics/index.ts';
import { createLocalWorkingDraftRepository } from '../persistence/index.ts';
import { TrainVisual, TrackVisual } from '../ride/track-visual.ts';
import {
  BunchaSceneService,
  createCoasterMaterials,
  disposeMaterialSet,
  recommendQualityTier,
} from '../scene/index.ts';
import type { CoasterProjectV1, TrackNode } from '../schema/index.ts';
import { TrackCompiler, type TableTrackSampler } from '../track/index.ts';
import type { SemanticSectionType } from '../track/index.ts';
import { transitionMode, type AppMode } from './mode.ts';
import {
  coasterFamilyForProject,
  compileProjectTrackResult,
  initialTrainReferenceDistance,
  type CompiledProjectTrack,
} from './ride-track.ts';
import {
  classifySimulatedIntensity,
  simulatedIntensityScore,
  type SimulatedIntensitySummary,
} from './ride-intensity.ts';
import type {
  CoasterRuntime,
  RuntimeDiagnostics,
  RuntimeIntensity,
  RuntimeRideResult,
  RuntimeSnapshot,
  RuntimeTelemetry,
} from './runtime-types.ts';

const MPH_PER_MPS = 2.2369362921;
const STALL_SPEED_MPS = 0.45;
const STALL_GRACE_SECONDS = 4;
const STALL_DURATION_SECONDS = 2;
const HISTORY_LIMIT = 20;
const EDITOR_CAMERA_FOV = 42;

const INITIAL_INTENSITY: RuntimeIntensity = {
  ...classifySimulatedIntensity({ vertical: 1, lateral: 0, longitudinal: 0 }),
  progressPercent: 0,
};

function fixtureFor(key: 'systems' | 'stall'): CoasterProjectV1 {
  return key === 'stall' ? stallHillCoasterFixture : systemsCircuitCoasterFixture;
}

function sectionLabel(type: SemanticSectionType | null): string {
  switch (type) {
    case 'station':
      return 'Station';
    case 'lift':
      return 'Lift';
    case 'launch':
      return 'Launch';
    case 'brake':
      return 'Brake';
    case 'normal':
      return 'Coasting';
    default:
      return 'Coasting';
  }
}

interface EditSnapshot {
  project: CoasterProjectV1;
  selectedNodeId: string | null;
}
function cloneProject(project: CoasterProjectV1): CoasterProjectV1 {
  return structuredClone(project);
}

function percentile(values: readonly number[], fraction: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * fraction))] ?? 0;
}

function asColor(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

function qualityTier(): 'low' | 'medium' | 'high' {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  return recommendQualityTier({
    mobile: matchMedia('(pointer: coarse)').matches || innerWidth < 720,
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    deviceMemoryGb: navigatorWithMemory.deviceMemory,
  });
}

class ProductionCoasterRuntime implements CoasterRuntime {
  private readonly listeners = new Set<(snapshot: RuntimeSnapshot) => void>();
  private readonly scene: BunchaSceneService;
  private readonly compiler = new TrackCompiler();
  private readonly dynamics = new TrackBoundDynamics();
  private readonly repository = createLocalWorkingDraftRepository();
  private readonly controls: OrbitControls;
  private readonly content = new THREE.Group();
  private readonly handleRoot = new THREE.Group();
  private readonly debugRoot = new THREE.Group();
  private readonly coasterMaterials;
  private readonly pointerEditor: PointerEditor;
  private readonly resizeObserver: ResizeObserver;
  private activeFixture: 'systems' | 'stall' = 'systems';
  private project = cloneProject(systemsCircuitCoasterFixture);
  private lastValidProject = cloneProject(systemsCircuitCoasterFixture);
  private compiledTrack!: CompiledProjectTrack;
  private sampler!: TableTrackSampler;
  private trackVisual!: TrackVisual;
  private trainVisual!: TrainVisual;
  private simulationState: TrackBoundState = createTrackBoundState(0, 2);
  private simulationFrame: SimulationFrame | null = null;
  private mode: AppMode = 'menu';
  private selectedNodeId: string | null = null;
  private status = 'Ready to build.';
  private debugVisible = location.hash === '#/debug';
  private persistenceAvailable = true;
  private readonly history: EditSnapshot[] = [];
  private editBaseline: EditSnapshot | null = null;
  private editCommitTimer: number | null = null;
  private continuousEdit = false;
  private rebuildMs = 0;
  private changedSpans = 0;
  private geometryRevision = 0;
  private peakSpeedMps = 0;
  private peakVerticalG = 1;
  private peakVerticalProgressPercent = 0;
  private peakLateralG = 0;
  private peakLateralProgressPercent = 0;
  private peakIntensity: RuntimeIntensity = { ...INITIAL_INTENSITY };
  private peakIntensityScore = 0;
  private readonly visitedSectionTypes = new Set<SemanticSectionType>();
  private ridePhase: 'idle' | 'running' | 'completed' | 'stalled' = 'idle';
  private rideDistanceM = 0;
  private rideNetDistanceM = 0;
  private rideElapsedSeconds = 0;
  private stallSeconds = 0;
  private lastRide: RuntimeRideResult | null = null;
  private accumulator = 0;
  private previousTime = performance.now();
  private animationFrame = 0;
  private pendingRebuild: number | null = null;
  private frameTimes: number[] = [];
  private disposed = false;
  private rideCameraNeedsSnap = true;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.scene = new BunchaSceneService({
      canvas,
      qualityTier: qualityTier(),
      themeId: 'park_model',
    });
    this.scene.camera.position.set(76, 58, 82);
    this.scene.camera.lookAt(0, 12, 0);
    this.controls = new OrbitControls(this.scene.camera, canvas);
    this.controls.target.set(0, 12, 0);
    this.controls.enableDamping = true;
    this.controls.minDistance = 25;
    this.controls.maxDistance = 180;
    this.controls.maxPolarAngle = Math.PI * 0.48;

    this.coasterMaterials = createCoasterMaterials({
      trackColor: asColor(this.project.appearance.trackColor),
      tieColor: 0x283744,
      supportColor: asColor(this.project.appearance.supportColor),
      trainColor: asColor(this.project.appearance.trainColor),
      stationColor: asColor(this.project.appearance.stationColor),
      stationRoofColor: 0x385768,
    });

    this.acceptCompiledTrack(this.compileTrack([]));
    this.trackVisual = new TrackVisual(this.compiledTrack, this.coasterMaterials);
    this.trainVisual = new TrainVisual(this.coasterMaterials.train, this.project.train.carCount);
    this.content.add(this.trackVisual.root, this.trainVisual.root, this.handleRoot, this.debugRoot);
    this.scene.replaceContentRoot(this.content);
    this.rebuildHandles();
    this.rebuildDebugFrames();
    this.placeTrain(initialTrainReferenceDistance(this.project, this.sampler));

    this.pointerEditor = new PointerEditor(canvas, this.scene.camera, this.handleRoot, {
      canEdit: () => this.mode === 'editor',
      onSelect: (nodeId) => {
        this.selectNodeById(nodeId);
        this.beginEdit(true);
        this.controls.enabled = false;
      },
      onMove: (nodeId, position) => {
        this.moveNode(nodeId, position);
      },
      onCommit: (nodeId, position) => {
        this.moveNode(nodeId, position);
        this.commitEdit();
      },
      onEnd: (changed) => {
        if (!changed) this.cancelEdit();
        this.controls.enabled = true;
      },
    });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas.parentElement ?? canvas);
    this.resize();
    this.setDebugVisible(this.debugVisible);
    this.handleRoot.visible = false;
    this.animationFrame = requestAnimationFrame(this.animate);
    void this.restoreLocalDraft();
  }

  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): RuntimeSnapshot {
    const selected = this.selectedNode();
    const selectedNodeIndex = selected ? this.project.track.nodes.indexOf(selected) : null;
    return {
      mode: this.mode,
      ready: true,
      status: this.status,
      selectedNodeId: this.selectedNodeId,
      selectedNodeLabel: selectedNodeIndex === null ? null : `Track Point ${selectedNodeIndex + 1}`,
      selectedNodeIndex,
      nodeCount: this.project.track.nodes.length,
      selectedHeightM: selected?.position.y ?? null,
      selectedPosition: selected ? { ...selected.position } : null,
      canUndo: this.history.length > 0,
      fixtureId: this.project.projectId,
      fixtureTitle: this.project.metadata.title,
      telemetry: this.telemetry(),
      ride: {
        phase: this.ridePhase,
        progressPercent: this.rideProgressPercent(),
        elapsedSeconds: this.rideElapsedSeconds,
        peakVerticalG: this.peakVerticalG,
        peakVerticalProgressPercent: this.peakVerticalProgressPercent,
        peakLateralG: this.peakLateralG,
        peakLateralProgressPercent: this.peakLateralProgressPercent,
        intensity: this.peakIntensity,
        visitedSectionTypes: [...this.visitedSectionTypes],
      },
      lastRide: this.lastRide,
      diagnostics: this.diagnostics(),
      debugVisible: this.debugVisible,
      persistenceAvailable: this.persistenceAvailable,
    };
  }

  openEditor(): void {
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = true;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setCameraFov(EDITOR_CAMERA_FOV);
    this.scene.camera.position.set(76, 58, 82);
    this.controls.target.set(0, 12, 0);
    this.controls.update();
    this.selectNodeById(
      this.project.track.nodes[Math.min(2, this.project.track.nodes.length - 1)]?.id ?? null,
    );
    this.status = 'Pick a point, shape the track, then test ride it.';
    this.emit();
  }

  startRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'editor') return;
    this.commitEdit();
    this.beginRide(camera);
  }

  restartRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.beginRide(camera);
  }

  private beginRide(camera: 'first-person' | 'third-person'): void {
    this.mode = transitionMode(this.mode, 'test');
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.handleRoot.visible = false;
    this.controls.enabled = false;
    this.simulationState = createTrackBoundState(
      initialTrainReferenceDistance(this.project, this.sampler),
      0,
    );
    this.simulationFrame = null;
    this.peakSpeedMps = 0;
    this.peakVerticalG = 1;
    this.peakVerticalProgressPercent = 0;
    this.peakLateralG = 0;
    this.peakLateralProgressPercent = 0;
    this.peakIntensity = { ...INITIAL_INTENSITY };
    this.peakIntensityScore = 0;
    this.visitedSectionTypes.clear();
    this.ridePhase = 'running';
    this.rideDistanceM = 0;
    this.rideNetDistanceM = 0;
    this.rideElapsedSeconds = 0;
    this.stallSeconds = 0;
    this.accumulator = 0;
    this.rideCameraNeedsSnap = true;
    this.setCameraFov(camera === 'first-person' ? 62 : 54);
    this.status = 'Ride in progress.';
    this.emit();
  }

  setRideCamera(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.rideCameraNeedsSnap = true;
    this.setCameraFov(camera === 'first-person' ? 62 : 54);
    this.emit();
  }

  returnToEditor(): void {
    if (
      this.mode !== 'ride-first-person' &&
      this.mode !== 'ride-third-person' &&
      this.mode !== 'test'
    )
      return;
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = true;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setCameraFov(EDITOR_CAMERA_FOV);
    this.scene.camera.position.set(76, 58, 82);
    this.controls.target.set(0, 12, 0);
    this.controls.update();
    if (!this.lastRide || this.ridePhase === 'running') this.finishRideResult('ended');
    const resultLabel =
      this.lastRide?.outcome === 'completed'
        ? 'Track complete.'
        : this.lastRide?.outcome === 'stalled'
          ? 'Ride stalled.'
          : 'Test ended.';
    this.ridePhase = 'idle';
    this.status = `${resultLabel} Top speed: ${(this.peakSpeedMps * MPH_PER_MPS).toFixed(1)} mph.`;
    this.emit();
  }

  resetFixture(): void {
    if (this.mode !== 'editor') return;
    this.cancelEdit();
    this.pushHistory({ project: cloneProject(this.project), selectedNodeId: this.selectedNodeId });
    this.replaceProject(cloneProject(fixtureFor(this.activeFixture)));
    this.selectNodeById(this.project.track.nodes[2]?.id ?? null);
    this.status = 'Track reset.';
    void this.saveDraft();
    this.emit();
  }

  loadDebugFixture(fixture: 'systems' | 'stall'): void {
    if (
      !this.debugVisible ||
      this.mode === 'ride-first-person' ||
      this.mode === 'ride-third-person'
    )
      return;
    this.cancelEdit();
    this.history.length = 0;
    this.activeFixture = fixture;
    this.replaceProject(cloneProject(fixtureFor(fixture)));
    this.selectNodeById(this.project.track.nodes[2]?.id ?? null);
    this.status = fixture === 'stall' ? 'Stall Test loaded.' : 'Systems Circuit loaded.';
    this.emit();
  }

  undo(): void {
    if (this.mode !== 'editor') return;
    const previous = this.history.pop();
    if (!previous) return;
    this.cancelEdit();
    this.replaceProject(cloneProject(previous.project));
    this.selectNodeById(previous.selectedNodeId);
    this.status = 'Last change undone.';
    void this.saveDraft();
    this.emit();
  }

  selectNode(index: number): void {
    if (this.mode !== 'editor') return;
    this.selectNodeById(this.project.track.nodes[index]?.id ?? null);
  }

  beginEdit(continuous = false): void {
    if (this.mode !== 'editor') return;
    this.continuousEdit ||= continuous;
    if (this.editBaseline) return;
    this.editBaseline = {
      project: cloneProject(this.project),
      selectedNodeId: this.selectedNodeId,
    };
  }

  commitEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    if (!this.editBaseline) return;
    this.pushHistory(this.editBaseline);
    this.editBaseline = null;
    this.continuousEdit = false;
    this.status = 'Track updated.';
    void this.saveDraft();
    this.emit();
  }

  setElevation(value: number): void {
    const node = this.selectedNode();
    if (!node || this.mode !== 'editor') return;
    this.beginEdit();
    this.updateNode(node.id, {
      x: node.position.x,
      y: THREE.MathUtils.clamp(value, 1, 48),
      z: node.position.z,
    });
    if (!this.continuousEdit) {
      if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = window.setTimeout(() => this.commitEdit(), 180);
    }
  }

  nudgeSelected(horizontal: number, forwardAmount: number): void {
    const node = this.selectedNode();
    if (!node || this.mode !== 'editor') return;
    this.beginEdit();
    const forward = new THREE.Vector3();
    this.scene.camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
    const offset = right.multiplyScalar(horizontal).addScaledVector(forward, forwardAmount);
    this.updateNode(node.id, {
      x: THREE.MathUtils.clamp(node.position.x + offset.x, -62, 62),
      y: node.position.y,
      z: THREE.MathUtils.clamp(node.position.z + offset.z, -42, 42),
    });
    this.commitEdit();
  }

  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    this.debugRoot.visible = visible;
    if (visible && location.hash !== '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}#/debug`);
    if (!visible && location.hash === '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}`);
    this.emit();
  }

  async captureEvidence(): Promise<Blob> {
    this.scene.render();
    const blob = await new Promise<Blob | null>((resolve) =>
      this.canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) throw new Error('The WebGL evidence frame could not be encoded.');
    return blob;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    cancelAnimationFrame(this.animationFrame);
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    this.resizeObserver.disconnect();
    this.pointerEditor.dispose();
    this.controls.dispose();
    this.trackVisual.dispose();
    this.trainVisual.dispose();
    disposeMaterialSet(this.coasterMaterials);
    this.scene.dispose();
    this.listeners.clear();
  }

  private selectedNode(): TrackNode | null {
    return this.project.track.nodes.find((node) => node.id === this.selectedNodeId) ?? null;
  }

  private selectNodeById(nodeId: string | null): void {
    this.selectedNodeId = nodeId;
    this.handleRoot.children.forEach((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const selected = child.userData.nodeId === nodeId;
      const material = child.material as THREE.MeshStandardMaterial;
      material.color.setHex(selected ? 0xc8fff1 : 0x5bd5ee);
      material.emissive.setHex(selected ? 0x2b8f82 : 0x123b4a);
      child.scale.setScalar(selected ? 1.25 : 1);
    });
    this.emit();
  }

  private cancelEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    this.editBaseline = null;
    this.continuousEdit = false;
  }

  private pushHistory(snapshot: EditSnapshot): void {
    this.history.push(snapshot);
    if (this.history.length > HISTORY_LIMIT) this.history.shift();
  }

  private replaceProject(project: CoasterProjectV1): void {
    if (this.pendingRebuild !== null) {
      cancelAnimationFrame(this.pendingRebuild);
      this.pendingRebuild = null;
    }
    const previousProject = this.project;
    this.project = project;
    let compiled: CompiledProjectTrack;
    let nextVisual: TrackVisual;
    try {
      compiled = this.compileTrack([]);
      nextVisual = new TrackVisual(compiled, this.coasterMaterials);
    } catch (error) {
      this.project = previousProject;
      throw error;
    }
    const previousVisual = this.trackVisual;
    this.content.remove(previousVisual.root);
    this.trackVisual = nextVisual;
    this.content.add(nextVisual.root);
    previousVisual.dispose();
    this.acceptCompiledTrack(compiled);
    this.lastValidProject = cloneProject(project);
    this.rebuildHandles();
    this.rebuildDebugFrames();
    this.placeTrain(initialTrainReferenceDistance(this.project, this.sampler));
  }

  private moveNode(nodeId: string, position: THREE.Vector3): void {
    this.updateNode(nodeId, {
      x: THREE.MathUtils.clamp(position.x, -62, 62),
      y: THREE.MathUtils.clamp(position.y, 1, 48),
      z: THREE.MathUtils.clamp(position.z, -42, 42),
    });
  }

  private updateNode(nodeId: string, position: { x: number; y: number; z: number }): void {
    const index = this.project.track.nodes.findIndex((node) => node.id === nodeId);
    if (index < 0) return;
    const mutable = cloneProject(this.project);
    mutable.track.nodes[index]!.position = position;
    mutable.metadata.updatedAt = new Date().toISOString();
    this.project = mutable;
    const handle = this.handleRoot.children.find((child) => child.userData.nodeId === nodeId);
    handle?.position.set(position.x, position.y, position.z);
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    this.pendingRebuild = requestAnimationFrame(() => {
      this.pendingRebuild = null;
      const started = performance.now();
      const before = this.compiler.counters;
      try {
        const compiled = this.compileTrack([index]);
        this.changedSpans = this.trackVisual.rebuild(compiled);
        this.acceptCompiledTrack(compiled);
        const after = this.compiler.counters;
        this.changedSpans = Math.max(
          this.changedSpans,
          after.invalidatedSpans - before.invalidatedSpans,
        );
        this.rebuildMs = performance.now() - started;
        this.geometryRevision += 1;
        this.lastValidProject = cloneProject(this.project);
        this.rebuildDebugFrames();
        this.placeTrain(initialTrainReferenceDistance(this.project, this.sampler));
        this.status = 'Track updated.';
      } catch {
        this.project = cloneProject(this.lastValidProject);
        this.rebuildHandles();
        this.selectNodeById(this.selectedNodeId);
        this.status = "That edit couldn't produce a continuous track. Restored the last version.";
      }
      this.emit();
    });
  }

  private compileTrack(changedIndices: readonly number[]): CompiledProjectTrack {
    return compileProjectTrackResult(this.project, this.compiler, changedIndices);
  }

  private acceptCompiledTrack(compiled: CompiledProjectTrack): void {
    this.compiledTrack = compiled;
    this.sampler = compiled.sampler;
  }

  private rebuildHandles(): void {
    while (this.handleRoot.children.length) {
      const child = this.handleRoot.children.pop();
      if (child instanceof THREE.Mesh) {
        child.children.forEach((label) => {
          if (!(label instanceof THREE.Sprite)) return;
          const material = label.material as THREE.SpriteMaterial;
          material.map?.dispose();
          material.dispose();
        });
        child.geometry.dispose();
        (child.material as THREE.Material).dispose();
      }
    }
    for (const [index, node] of this.project.track.nodes.entries()) {
      const handle = new THREE.Mesh(
        new THREE.SphereGeometry(1.25, 16, 10),
        new THREE.MeshStandardMaterial({ color: 0x5bd5ee, emissive: 0x123b4a, roughness: 0.25 }),
      );
      handle.position.set(node.position.x, node.position.y, node.position.z);
      handle.userData.nodeId = node.id;
      handle.userData.nodeIndex = index;
      handle.castShadow = false;
      handle.add(this.createPointLabel(index + 1));
      this.handleRoot.add(handle);
    }
  }

  private createPointLabel(pointNumber: number): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = '#07131a';
      context.beginPath();
      context.arc(48, 48, 38, 0, Math.PI * 2);
      context.fill();
      context.lineWidth = 7;
      context.strokeStyle = '#c8fff1';
      context.stroke();
      context.fillStyle = '#ffffff';
      context.font = '700 46px system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(String(pointNumber), 48, 50);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    label.position.set(0, 2.7, 0);
    label.scale.set(3.5, 3.5, 1);
    label.renderOrder = 50;
    return label;
  }

  private rebuildDebugFrames(): void {
    while (this.debugRoot.children.length) this.debugRoot.remove(this.debugRoot.children[0]!);
    const frameCount = 18;
    for (let index = 0; index < frameCount; index += 1) {
      const sample = this.sampler.sampleByDistance((index / frameCount) * this.sampler.length);
      const origin = new THREE.Vector3(sample.position.x, sample.position.y, sample.position.z);
      this.debugRoot.add(
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.up.x, sample.up.y, sample.up.z),
          origin,
          2.4,
          0x78e3cc,
          0.4,
          0.2,
        ),
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.right.x, sample.right.y, sample.right.z),
          origin,
          1.8,
          0xf2c868,
          0.35,
          0.18,
        ),
      );
    }
    this.debugRoot.visible = this.debugVisible;
  }

  private placeTrain(distance: number): void {
    this.trainVisual?.place(this.sampler, distance, this.project.train.carSpacingMeters);
  }

  private setCameraFov(fov: number): void {
    if (Math.abs(this.scene.camera.fov - fov) < 0.01) return;
    this.scene.camera.fov = fov;
    this.scene.camera.updateProjectionMatrix();
  }

  private updateRideCamera(frame: SimulationFrame): void {
    const lead = frame.cars[0];
    if (!lead) return;
    const position = new THREE.Vector3(lead.position.x, lead.position.y, lead.position.z);
    const forward = new THREE.Vector3(lead.forward.x, lead.forward.y, lead.forward.z);
    const up = new THREE.Vector3(lead.up.x, lead.up.y, lead.up.z);
    const worldUp = new THREE.Vector3(0, 1, 0);
    const lookAheadDistance = THREE.MathUtils.clamp(frame.referenceState.velocity * 0.8, 10, 22);
    const future = this.sampler.sampleByDistance(lead.distance + lookAheadDistance);
    const futurePosition = new THREE.Vector3(
      future.position.x,
      future.position.y,
      future.position.z,
    );
    const futureUp = new THREE.Vector3(future.up.x, future.up.y, future.up.z);
    if (this.mode === 'ride-first-person') {
      this.scene.camera.position
        .copy(position)
        .addScaledVector(up, 1.65)
        .addScaledVector(forward, 1.55);
      this.scene.camera.up.copy(up).lerp(worldUp, 0.35).normalize();
      this.scene.camera.lookAt(futurePosition.addScaledVector(futureUp, 0.3));
    } else {
      const chaseDirection = forward.clone();
      chaseDirection.y *= 0.35;
      if (chaseDirection.lengthSq() < 1e-6) chaseDirection.set(0, 0, -1);
      chaseDirection.normalize();
      const desired = position
        .clone()
        .addScaledVector(worldUp, 8)
        .addScaledVector(chaseDirection, -21);
      if (this.rideCameraNeedsSnap) this.scene.camera.position.copy(desired);
      else this.scene.camera.position.lerp(desired, 0.12);
      this.scene.camera.up.copy(worldUp);
      this.scene.camera.lookAt(futurePosition.addScaledVector(worldUp, 1.5));
    }
    this.rideCameraNeedsSnap = false;
  }

  private rideProgressPercent(): number {
    if (this.ridePhase === 'completed') return 100;
    if (!this.sampler?.length) return 0;
    return THREE.MathUtils.clamp((this.rideDistanceM / this.sampler.length) * 100, 0, 100);
  }

  private finishRideResult(outcome: RuntimeRideResult['outcome']): void {
    this.lastRide = {
      outcome,
      progressPercent: this.rideProgressPercent(),
      elapsedSeconds: this.rideElapsedSeconds,
      peakSpeedMph: this.peakSpeedMps * MPH_PER_MPS,
      peakVerticalG: this.peakVerticalG,
      peakVerticalProgressPercent: this.peakVerticalProgressPercent,
      peakLateralG: this.peakLateralG,
      peakLateralProgressPercent: this.peakLateralProgressPercent,
      intensity: this.peakIntensity,
      visitedSectionTypes: [...this.visitedSectionTypes],
    };
  }

  private endRide(outcome: 'completed' | 'stalled'): void {
    if (this.ridePhase !== 'running') return;
    this.ridePhase = outcome;
    this.finishRideResult(outcome);
    this.status =
      outcome === 'completed'
        ? 'Track complete! Your train finished the circuit.'
        : 'Ride stalled. Lower the next hill or add more speed.';
    this.emit();
  }

  private updateRideOutcome(previous: TrackBoundState, frame: SimulationFrame): void {
    const next = frame.referenceState;
    let distanceDelta = next.sRef - previous.sRef;
    if (next.direction === 1 && distanceDelta < -this.sampler.length / 2)
      distanceDelta += this.sampler.length;
    if (next.direction === -1 && distanceDelta > this.sampler.length / 2)
      distanceDelta -= this.sampler.length;
    this.rideNetDistanceM += distanceDelta;
    this.rideDistanceM = Math.max(this.rideDistanceM, this.rideNetDistanceM);
    this.rideElapsedSeconds += FIXED_PHYSICS_STEP;
    const force = frame.forces[0];
    const activeSection = this.sampler.sampleByDistance(next.sRef).section?.type ?? null;
    const stationHeld =
      activeSection === 'station' &&
      (next.sectionRuntime.station as { released?: unknown } | undefined)?.released === false;
    this.stallSeconds =
      next.velocity < STALL_SPEED_MPS && !stationHeld ? this.stallSeconds + FIXED_PHYSICS_STEP : 0;
    if (activeSection) this.visitedSectionTypes.add(activeSection);
    if (force) {
      const windowedForce = force.windows['200'] ?? force;
      const progressPercent = THREE.MathUtils.clamp(
        (force.distance / this.sampler.length) * 100,
        0,
        100,
      );
      if (Math.abs(windowedForce.vertical) > this.peakVerticalG) {
        this.peakVerticalG = Math.abs(windowedForce.vertical);
        this.peakVerticalProgressPercent = progressPercent;
      }
      if (Math.abs(windowedForce.lateral) > this.peakLateralG) {
        this.peakLateralG = Math.abs(windowedForce.lateral);
        this.peakLateralProgressPercent = progressPercent;
      }
      const intensityScore = simulatedIntensityScore(windowedForce);
      if (intensityScore > this.peakIntensityScore) {
        const intensity: SimulatedIntensitySummary = classifySimulatedIntensity(windowedForce);
        this.peakIntensityScore = intensityScore;
        this.peakIntensity = { ...intensity, progressPercent };
      }
    }

    if (this.rideDistanceM >= this.sampler.length * 0.995) this.endRide('completed');
    else if (
      this.rideElapsedSeconds >= STALL_GRACE_SECONDS &&
      this.stallSeconds >= STALL_DURATION_SECONDS
    )
      this.endRide('stalled');
  }

  private telemetry(): RuntimeTelemetry {
    const force = this.simulationFrame?.forces[0];
    const speed = this.simulationFrame?.referenceState.velocity ?? 0;
    const activeSectionType = this.simulationFrame
      ? (this.sampler.sampleByDistance(this.simulationFrame.referenceState.sRef).section?.type ??
        null)
      : null;
    return {
      speedMps: speed,
      speedMph: speed * MPH_PER_MPS,
      verticalG: force?.vertical ?? 1,
      lateralG: force?.lateral ?? 0,
      longitudinalG: force?.longitudinal ?? 0,
      peakSpeedMph: this.peakSpeedMps * MPH_PER_MPS,
      activeSectionType,
      activeSectionLabel: sectionLabel(activeSectionType),
    };
  }

  private diagnostics(): RuntimeDiagnostics {
    const renderer = this.scene.renderer;
    const info = renderer instanceof THREE.WebGLRenderer ? renderer.info : null;
    const continuity = this.trackVisual.continuity();
    return {
      qualityTier: this.scene.quality.tier,
      frameMsP50: percentile(this.frameTimes, 0.5),
      frameMsP95: percentile(this.frameTimes, 0.95),
      rebuildMs: this.rebuildMs,
      changedSpans: this.changedSpans,
      geometryRevision: this.geometryRevision,
      trackLengthM: this.sampler.length,
      sampleCount: this.sampler.table.count,
      rendererGeometries: info?.memory.geometries ?? 0,
      rendererTextures: info?.memory.textures ?? 0,
      maxRailSeamGapM: continuity.maxRailGapMeters,
      maxRailSeamAngleDegrees: continuity.maxRailNormalAngleDegrees,
      maxTieSpacingM: continuity.maxTieSpacingMeters,
    };
  }

  private readonly animate = (time: number): void => {
    if (this.disposed) return;
    const frameMs = Math.min(100, time - this.previousTime);
    this.previousTime = time;
    this.frameTimes.push(frameMs);
    if (this.frameTimes.length > 600) this.frameTimes.shift();
    if (
      (this.mode === 'ride-first-person' || this.mode === 'ride-third-person') &&
      this.ridePhase === 'running'
    ) {
      this.accumulator = Math.min(0.25, this.accumulator + frameMs / 1000);
      while (this.accumulator >= FIXED_PHYSICS_STEP) {
        const previousState = this.simulationState;
        this.simulationFrame = this.dynamics.step(
          previousState,
          { sampler: this.sampler, family: coasterFamilyForProject(this.project) },
          FIXED_PHYSICS_STEP,
        );
        this.simulationState = this.simulationFrame.referenceState;
        this.peakSpeedMps = Math.max(this.peakSpeedMps, this.simulationState.velocity);
        this.updateRideOutcome(previousState, this.simulationFrame);
        this.accumulator -= FIXED_PHYSICS_STEP;
        if (this.ridePhase !== 'running') break;
      }
      if (this.simulationFrame) {
        this.placeTrain(this.simulationFrame.referenceState.sRef);
        this.updateRideCamera(this.simulationFrame);
      }
    } else {
      this.controls.update();
    }
    this.scene.render();
    if (Math.floor(time / 100) !== Math.floor((time - frameMs) / 100)) this.emit();
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  private resize(): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.scene.resize(bounds.width, bounds.height);
  }

  private async saveDraft(): Promise<void> {
    const result = await this.repository.save(this.project);
    this.persistenceAvailable = result.ok || result.error.code !== 'unavailable';
    if (!result.ok) this.status = "Couldn't save this change.";
    this.emit();
  }

  private async restoreLocalDraft(): Promise<void> {
    const result = await this.repository.load(this.project.projectId);
    if (result.ok) {
      this.replaceProject(result.value.project);
      this.status = 'Saved build loaded.';
    } else if (result.error.code === 'unavailable') {
      this.persistenceAvailable = false;
      this.status = 'Saving is off. You can still build and ride.';
    }
    this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

export function createCoasterRuntime(canvas: HTMLCanvasElement): CoasterRuntime {
  return new ProductionCoasterRuntime(canvas);
}
